/* eslint-disable @next/next/no-css-tags */
import { GetServerSidePropsContext, NextPage } from "next";
import Head from "next/head";
import { ENDPOINT_CONTENT_ID } from "lib/graphql/endpoints";
import { VEHICLES_LIST } from "lib/graphql/queries/vehicles";
import { replaceStyleTag, stringMinify } from "utils/StringUtils";
import { VehicleResponse } from "models/vehicleResponse";
import getSettings from "utils/getSettings";
import { useContext, useEffect, useMemo, useReducer } from "react";
import { VehicleContext } from "context/vehicleContext";
import useResize from "components/Hooks/useResize";
import dynamic from "next/dynamic";
import { DeviceEnum } from "models/enums";
import InfiniteScrollSRP from "components/vehicles/SRP/InfiniteScrollSRP";
import useFilterMethods from "components/Hooks/useFiltersMethods";
import VehicleReducer, { ACTION_TYPES } from "reducers/vehicleReducer";
import { useRouter } from "next/router";
import { addParamToVehicleUrl, removeParamFromUrl } from "utils/commonUtils";
import { FiltersDB } from "models/filtersDB";
import { AppContext } from "context/appContext";
import { v4 as uuidv4 } from 'uuid';
import defaultParameters from "models/parametersModel";
import RenderScriptsContent from "components/RenderScripts/RenderScriptsContent";

const AdvanceSearch = dynamic(
    () => import("components/vehicles/SRP/AdvanceSearch")
);
const CardView = dynamic(() => import("components/vehicles/SRP/CardView"));
const GridView = dynamic(() => import("components/vehicles/SRP/GridView"));
const ListView = dynamic(() => import("components/vehicles/SRP/ListView"));
const Pagination = dynamic(() => import("components/vehicles/SRP/Pagination"));
const SearchBar = dynamic(() => import("components/vehicles/SRP/SearchBar"));

const SRP: NextPage = ({
    titlePage,
    companyInfo,
    srpSettings,
    vehicleResponse,
    keyword,
    settings,
    paramsFromUrl,
    currentLocation,
}: any) => {
    const router = useRouter();
    const { whichDevice } = useResize();

    const thirdParty = settings.thirdParty;

    const { openFilters } = useContext(AppContext);

    const { useCriteoTags, criteoScript, applyCriteoSRP } = thirdParty;

    const { 
        getVehicleFilters,
        prepareFiltersData,
        filtersFromUrl,
        isFilteredFromUrl,
    } = useFilterMethods(paramsFromUrl);

    const initState = {
        originalFilterData: [],
        filters: [],
        filtersData: [],
        dropdownOpened: {
            style: false,
            make: false,
            model: false,
            transmission: false,
            lotname: false,
        },
        isLoadingVehicles: false,
        isLoadingFilters: false,
        sortBy: paramsFromUrl.sort,
        showQuantity: paramsFromUrl.show ?? 20,
        currentPage: Number(paramsFromUrl.page) ?? 1,
        viewType: srpSettings.tv || srpSettings.inventoryStyleView || paramsFromUrl.tv || "list",
        filtersHash: uuidv4(),
        vehicleHash: '',
    };
  
    const [state, dispatch] = useReducer(VehicleReducer, initState);

    const {
        originalFilterData, isLoadingVehicles,
        filtersData, filtersHash, vehicleHash } = state;

    const isMobileDevice = whichDevice === DeviceEnum.MOBILE;
    const usePagination = srpSettings.pagingType === "pagination";
    const isCardViewLayout = srpSettings.srpLayout === "cardViewLayout";
    const viewType = srpSettings.tv || srpSettings.inventoryStyleView || "list";
    const lockedSearchAtTop = srpSettings.lockedSearchAtTop;
    const styleInline = replaceStyleTag(srpSettings.styles);

    function setLoadingVehicles(val: boolean) {
        dispatch({
            type: ACTION_TYPES.SET_LOADING_VEHICLES,
            payload: {
                isLoadingVehicles: val,
            },
        });
    }

    function setLoadingFilters(val: boolean) {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            payload: {
                isLoadingFilters: val,
            },
        });
    }

    const fetchFilter = async () => {
        const dbFilters = await getVehicleFilters();
        dispatch({
            type: ACTION_TYPES.SET_ORIGINAL_FILTER_DATA,
            payload: dbFilters,
            runMethodPrepareFilter: prepareFiltersData,
            filtersFromUrl,
        });
    };

    const setStateValues = (values: any) => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            payload: {
                isLoadingVehicles: true,
                currentPage: 0,
                ...values,
            },
        });
    };

    useEffect(() => {
        if((!isMobileDevice || (isMobileDevice && isFilteredFromUrl)) && filtersData.length === 0)
        {
            fetchFilter();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobileDevice]);

    // this is to hide the spinner
    useEffect(() => {
        setLoadingVehicles(false);
    },[vehicleResponse.hash]);

    if(isMobileDevice && filtersData.length === 0 && openFilters)
    {
        fetchFilter();
    }

    const data = vehicleResponse?.data ?? [];
    const posibleMatchList = vehicleResponse?.posibleMatch ?? [];

    const skeleton = [1,2,3,4,5,6,7,8,9,10,11,12];

    const vehicleContextValue = useMemo(() => {
        const resetFiltersClearAll = async() => {
            const initDefaultParams =JSON.parse(JSON.stringify(defaultParameters));
            const dbFilters = originalFilterData?.length > 0 ? originalFilterData : await getVehicleFilters();
            dispatch({
                type: ACTION_TYPES.SET_ORIGINAL_FILTER_DATA,
                payload: dbFilters,
                runMethodPrepareFilter: prepareFiltersData,
                filtersFromUrl: initDefaultParams,
            });
            router.push("/vehicles");
        };
    
        const onSetDropdownOpen = (filterName: string, val: boolean) => {
            dispatch({
                type: ACTION_TYPES.SET_OPEN_DROPDOWN,
                payload: {
                    filterName,
                    val,
                },
            });
        };
    
        const setSelectedItemInDropdown = (filterName: string, val: any) => {
            const dataDropdown = filtersData?.filter((c: FiltersDB) => c.typeName.toLowerCase() === filterName.toLowerCase());
            const itemsSelected: string[] = [];
            dataDropdown.forEach((item: FiltersDB) => {
                const isSelected = val.keyName.toLowerCase() === item.keyName.toLowerCase()
                    ? !val.isSelected
                    : item.isSelected;
    
                if (isSelected) itemsSelected.push(item.keyName);
            });
            setFilter(filterName, itemsSelected);
            dispatch({
                type: ACTION_TYPES.SET_FILTER,
                payload: {
                    filterName,
                    values: itemsSelected,
                    runMethodPrepareFilter: prepareFiltersData,
                    filtersFromUrl,
                },
            });
        };
    
        const setSelectedItemInSlider = (filterName: string, val: any, currentRange: FiltersDB, valueReseted: boolean) => {
            dispatch({
                type: ACTION_TYPES.SET_FILTER_SLIDER,
                payload: {
                    filterName,
                    values: valueReseted ? [] : val,
                    runMethodPrepareFilter: prepareFiltersData,
                    filtersFromUrl,
                    previousValues: currentRange,
                    valueReseted,
                },
            });
    
            if(valueReseted){
                let url = addParamToVehicleUrl("page", "1");
                url = removeParamFromUrl('search', url);
                url = removeParamFromUrl(filterName, url);
                router.replace(url);
            } else setFilter(filterName, val);
        };
    
        const setFilter = (filterName: string, value: string[]) => {
            setLoadingVehicles(true);
            let url = addParamToVehicleUrl("page", "1");
            url = removeParamFromUrl('search', url);
            url = addParamToVehicleUrl(filterName, value.join("|"), url);
            router.replace(url);
        };
    
        const resetFiltersDueToSearch = () =>{
            dispatch({
                type: ACTION_TYPES.RESET_FILTERS_BY_SEARCH,
                payload: {
                    runMethodPrepareFilter: prepareFiltersData,
                },
            });
        };

        const methods = {
            resetFiltersClearAll,
            setLoadingVehicles,
            setLoadingFilters,
            prepareFiltersData,
            onSetDropdownOpen,
            setFilter,
            setSelectedItemInDropdown,
            setSelectedItemInSlider,
            resetFiltersDueToSearch,
            setStateValues,
        };

        return {
            settings,
            paramsFromUrl:{
                ...paramsFromUrl,
                isFilteredFromUrl
            },
            currentState: state,
            originalFilterData,
            currentLocation,
            filtersFromUrl,
            vehicleResponse,
            ...methods,
        };
    }, [currentLocation, filtersData, filtersFromUrl, getVehicleFilters, isFilteredFromUrl, originalFilterData, paramsFromUrl, prepareFiltersData, router, settings, state, vehicleResponse]);

    return (
        <VehicleContext.Provider
            value={vehicleContextValue}
        >
            <Head>
                <title>{titlePage}</title>
                {usePagination && (
                    <link
                        rel="preload"
                        href="/static/styles/vehicle-simplePagination.min.css"
                        as="style"
                    />
                )}

                <link rel="preload" href="/static/styles/vehicles.min.css" as="style" />
                <link
                    rel="preload"
                    href="/static/styles/vehicle-filters.min.css"
                    as="style"
                />
                <link
                    rel="preload"
                    href="/static/styles/searchtext.min.css"
                    as="style"
                />
                {isCardViewLayout && (
                    <link
                        rel="preload"
                        href="/static/styles/vehicleCardViewLayout.min.css"
                        as="style"
                    />
                )}
                {lockedSearchAtTop && isMobileDevice && (
                    <link
                        rel="preload"
                        href="/static/styles/vehicle-lockedSearchTop.min.css"
                        as="style"
                    />
                )}
                <link
                    rel="preload"
                    href="/static/styles/multiRangeSlider.min.css"
                    as="style"
                />

                {usePagination && (
                    <link
                        rel="stylesheet"
                        href="/static/styles/vehicle-simplePagination.min.css"
                    />
                )}
                <link rel="stylesheet" href="/static/styles/vehicles.min.css" />
                <link rel="stylesheet" href="/static/styles/vehicle-filters.min.css" />
                <link rel="stylesheet" href="/static/styles/searchtext.min.css" />
                {isCardViewLayout && (
                    <link
                        rel="stylesheet"
                        href="/static/styles/vehicleCardViewLayout.min.css"
                    />
                )}
                {lockedSearchAtTop && isMobileDevice && (
                    <link
                        rel="stylesheet"
                        href="/static/styles/vehicle-lockedSearchTop.min.css"
                    />
                )}
                <link rel="stylesheet" href="/static/styles/multiRangeSlider.min.css" />
                <link
                    rel="preload"
                    as="image"
                    imageSrcSet="https://automatrix-cms.mo.cloudinary.net/content/1/6/images/vehicle-placeholder.jpg?tx=w_10,q_1 10w"
                    imageSizes="100vw"
                />
                <link
                    rel="preload"
                    as="image"
                    imageSrcSet="/static/images/CarInTransit.png?tx=w_400,q_1 400w"
                    imageSizes="100vw"
                />
            </Head>

            {styleInline && (<style jsx>{`${stringMinify(styleInline)}`}</style>)}
            {useCriteoTags && applyCriteoSRP &&
                    <RenderScriptsContent scriptContent={criteoScript} scriptPosition="scriptOnHead"/>
            }

            <section className="product-listing">
                <div className="ax-vehicles-sidebar">
                    <AdvanceSearch
                        thirdParty={thirdParty}
                        srpSettings={srpSettings}
                        companyInfo={companyInfo}
                        lockedSearchAtTop={lockedSearchAtTop}
                        currentUrl={currentLocation}
                    />
                </div>
                <div className="ax-vehicles-content active isfiltered">
                    <SearchBar
                        srpSettings={srpSettings}
                        companyInfo={companyInfo}
                        keyword={keyword}
                        filtersHash={filtersHash}
                    />
                    {data?.length === 0 && keyword !== "" && (
                        <div className="content-vehicles">
                            <div
                                className="alert alert-danger alert-dismissible messageinfovehicles text-center"
                                role="alert"
                            >
                                <span id="messagefitertouser">
                                    Whoops! We did not find anything under search: {keyword}
                                </span>
                                {posibleMatchList?.length > 0 && (
                                    <>
                                        <br/>
                                        <span>Did you mean:</span>
                                        {posibleMatchList.map((itemMatch: any) => (<><br/><a target='_self' href={`/vehicles?search=${itemMatch.result}&page=1`}>{itemMatch.result}</a></>))}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {data.length > 0 && <>
                        {isCardViewLayout && (
                            <div className="content-vehicles ax-vehicle-list">
                                {!isLoadingVehicles && !usePagination && (
                                    <InfiniteScrollSRP
                                        dataLength={data.length}
                                        totalDataLenght={vehicleResponse.totalSearch}
                                    >
                                        <CardView
                                            srpSettings={srpSettings}
                                            thirdParty={thirdParty}
                                            companyInfo={companyInfo}
                                            items={data}
                                            vehicleHash={vehicleHash}
                                            currentUrl={currentLocation}
                                        />
                                    </InfiniteScrollSRP>
                                )}
                                {!isLoadingVehicles && usePagination && (
                                    <CardView
                                        srpSettings={srpSettings}
                                        thirdParty={thirdParty}
                                        companyInfo={companyInfo}
                                        items={data}
                                        vehicleHash={vehicleHash}
                                        currentUrl={currentLocation}
                                    />
                                )}
                            </div>
                        )}

                        {!isCardViewLayout && viewType === "list" && (
                            <div className="content-vehicles ax-vehicle-list list-view">
                                {!isLoadingVehicles && <ListView
                                    srpSettings={srpSettings}
                                    thirdParty={thirdParty}
                                    companyInfo={companyInfo}
                                    items={data}
                                />
                                }
                            </div>
                        )}

                        {!isCardViewLayout && viewType === "grid" && (
                            <div className="content-vehicles ax-vehicle-list">
                                {!isLoadingVehicles && <GridView
                                    srpSettings={srpSettings}
                                    thirdParty={thirdParty}
                                    companyInfo={companyInfo}
                                    items={data}
                                />}
                            </div>
                        )}
                    </>
                    }
                    {isLoadingVehicles &&
                        <div id="infinitegifsrp" className="text-center col-md-12 skeleton-grid">
                            {skeleton.map((a,i)=><div key={i} className="skeleton-srp skeleton-glow col-md-3"></div>)}
                        </div>
                    }
                    {usePagination && data.length > 0 && (
                        <>             
                            <div
                                className="col-lg-12 light-theme simple-pagination"
                                id="pagination"
                            >
                                <Pagination />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </VehicleContext.Provider>
    );
};

export const getServerSideProps = async (props: GetServerSidePropsContext) => {
    const { query, res, req, resolvedUrl } = props;

    res.setHeader(
        "Cache-Control",
        `public, s-maxage=${process.env.CACHE_CONTROL_MAX}, stale-while-revalidate=${process.env.CACHE_CONTROL_MAX}`
    );

    const {
        search = "",
        paymentAmount = "",
        internetPrice = "",
        miles = "",
        year = "",
        style = "",
        make = "",
        model = "",
        transmission = "",
        lotName = "",
        tv = "grid",
        sort = "default",
        page = 0,
        show = 20,
    } = query as any;

    const contentIds = [ENDPOINT_CONTENT_ID.SRP_SETTINGS];
    const { client, settings, websiteContent } = await getSettings(req, contentIds);

    try {
        const content =
        websiteContent?.find(
            (item) => item?.key === ENDPOINT_CONTENT_ID.SRP_SETTINGS
        ) || null;
        const srpSettings = JSON.parse(stringMinify(content?.value || ""));

        const sortField = sort === "default"
            ? srpSettings?.defaultSort?.split(" ") ?? ["stocknumber", "asc"]
            : sort?.split("|");
        
        const filters = {
            paymentAmount: {
                filterName: "PaymentAmount",
                isFiltered: paymentAmount !== "",
                values: paymentAmount?.split("|") ?? [],
            },
            internetPrice: {
                filterName: "internetPrice",
                isFiltered: internetPrice !== "",
                values: internetPrice?.split("|") ?? [],
            },
            miles: {
                filterName: "Miles",
                isFiltered: miles !== "",
                values: miles?.split("|") ?? [],
            },
            year: {
                filterName: "Year",
                isFiltered: year !== "",
                values: year?.split("|") ?? [],
            },
            style: {
                filterName: "Style",
                isFiltered: style !== "",
                values: style?.split("|") ?? [],
            },
            make: {
                filterName: "Make",
                isFiltered: make !== "",
                values: make?.split("|") ?? [],
            },
            model: {
                filterName: "Model",
                isFiltered: model !== "",
                values: model?.split("|") ?? [],
            },
            transmission: {
                filterName: "Transmission",
                isFiltered: transmission !== "",
                values: transmission?.split("|") ?? [],
            },
            lotName: {
                filterName: "LotName",
                isFiltered: lotName !== "",
                values: lotName?.split("|") ?? [],
            },
            keyword: search,
        };

        const validShow = isNaN(show) ? 20 : Number(show);
        const currentOffset = show === "all" ? 0 : Number(Number(page) - 1 < 0 ? 0 : Number(page) - 1) * validShow;
        const validSort = ['stocknumber', 'make', 'year', 'miles','price', 'internetprice'].includes(sortField?.[0]?.toLowerCase()) ? sortField?.[0]?.toLowerCase() : "internetprice";
        const validDir = ['asc', 'desc'].includes(sortField?.[1]?.toLowerCase()) ? sortField?.[1]?.toLowerCase() : "asc";

        const { data: dataItems } = await client.query<VehicleResponse>({
            query: VEHICLES_LIST,
            variables: {
                ignorePaging: show === "all" ? true : false,
                paginate: {
                    start: currentOffset,
                    limit: show === "all" ? 0 : validShow,
                },
                filters,
                sort: {
                    field: validSort,
                    dir: validDir,
                },
                restrictedLots: process.env.RESTRICTED_LOTS,
            },
            fetchPolicy: 'no-cache',
        });

        return {
            props: {
                titlePage: srpSettings.titlePage ?? "Vehicles list",
                friendlyUrl: srpSettings.friendlyUrl,
                keywords: srpSettings.keywords,
                metaDescription: srpSettings.metaDescription,
                companyInfo: settings.companyInfo,
                srpSettings: {
                    ...srpSettings,
                    tv,
                },
                keyword: search,
                settings: settings,
                page: "srp",
                paramsFromUrl: {
                    ...query,
                    keyword: search ?? '',
                    page,
                    tv,
                    sort,
                    show,
                    sortField,
                },
                vehicleResponse: {
                    ...dataItems.getWebsiteVehicleList,
                    hash: uuidv4(),
                },
                currentLocation: resolvedUrl,
            },
        };
    } catch {
        return {
            notFound: true,
        };
    }
};

export default SRP;
